import { template as template_6fe4a593d63a4b498cc3c52cebb7bf7f } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { task as trackedTask } from 'reactiveweb/ember-concurrency';
import TaskStatuses from 'eflex/constants/task-statuses';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import TabulatorTable from 'eflex/components/tabulator-table';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { t } from 'ember-intl';
import BiTotalCyclesLabel from 'eflex/components/bi/total-cycles-label';
import BiNoDataPlaceholder from 'eflex/components/bi/no-data-placeholder';
import Spinner from 'eflex/components/spinner';
import { isTesting, macroCondition } from '@embroider/macros';
const REFRESH_INTERVAL = 60_000;
export default class AndonProductionStation extends Component {
    @service
    queryRunner;
    @service
    intl;
    @tracked
    timerToken;
    startAutoRefresh = task({
        drop: true
    }, waitFor(async ()=>{
        if (macroCondition(isTesting())) {
            return;
        }
        if (globalThis.isIntegrationTest) {
            return;
        }
        while(true){
            await timeout(REFRESH_INTERVAL);
            this.timerToken = true;
        }
    }));
    get params() {
        return {
            stationIds: this.args.stationIds,
            modelIds: this.args.modelIds,
            userIds: this.args.userIds,
            tags: this.args.tags,
            startDate: this.args.startDate,
            endDate: this.args.endDate,
            shiftNames: this.args.shiftNames,
            useTotalCycles: this.args.useTotalCycles
        };
    }
    get tableConfiguration() {
        return {
            columns: [
                {
                    title: this.intl.t('station'),
                    field: 'station',
                    tooltip: false,
                    headerSort: false,
                    formatter: 'textarea'
                },
                {
                    title: this.intl.t('bi.chartLabel.partCounts'),
                    field: 'partCounts',
                    tooltip: false,
                    headerSort: false,
                    formatter: (cell)=>this.formatPartsColumnData(cell),
                    hozAlign: 'right'
                }
            ]
        };
    }
    getData = task({
        restartable: true
    }, waitFor(async (params)=>{
        const totalCyclesTableQuery = await this.queryRunner.queryWithParams.perform(params, [
            {
                $group: {
                    _id: '$location._id',
                    station: {
                        $first: '$location.captions'
                    },
                    totalParts: {
                        $sum: 1
                    },
                    goodParts: {
                        $sum: {
                            $cond: {
                                if: {
                                    $and: [
                                        {
                                            $gte: [
                                                '$status',
                                                TaskStatuses.GOOD_RANGE_START
                                            ]
                                        },
                                        {
                                            $lte: [
                                                '$status',
                                                TaskStatuses.GOOD_RANGE_END
                                            ]
                                        }
                                    ]
                                },
                                then: 1,
                                else: 0
                            }
                        }
                    }
                }
            },
            {
                $project: {
                    _id: false,
                    station: {
                        $arrayElemAt: [
                            '$station',
                            0
                        ]
                    },
                    totalParts: true,
                    goodParts: true
                }
            },
            {
                $sort: {
                    totalParts: -1
                }
            }
        ]);
        const totalPartsTableQuery = await this.queryRunner.queryWithParams.perform(params, [
            {
                $sort: {
                    timestamp: -1
                }
            },
            {
                $group: {
                    _id: {
                        serialNumber: '$serialNumber',
                        stationId: '$location._id'
                    },
                    buildStatus: {
                        $first: {
                            station: '$location',
                            timestamp: '$timestamp',
                            status: '$status'
                        }
                    }
                }
            },
            {
                $group: {
                    _id: '$_id.stationId',
                    station: {
                        $first: '$buildStatus.station.captions'
                    },
                    totalParts: {
                        $sum: 1
                    },
                    goodParts: {
                        $sum: {
                            $cond: {
                                if: {
                                    $and: [
                                        {
                                            $gte: [
                                                '$buildStatus.status',
                                                TaskStatuses.GOOD_RANGE_START
                                            ]
                                        },
                                        {
                                            $lte: [
                                                '$buildStatus.status',
                                                TaskStatuses.GOOD_RANGE_END
                                            ]
                                        }
                                    ]
                                },
                                then: 1,
                                else: 0
                            }
                        }
                    }
                }
            },
            {
                $project: {
                    _id: 0,
                    station: {
                        $arrayElemAt: [
                            '$station',
                            0
                        ]
                    },
                    totalParts: true,
                    goodParts: true
                }
            },
            {
                $sort: {
                    totalParts: -1
                }
            }
        ]);
        const andonProductionQuery = params.useTotalCycles ? totalCyclesTableQuery : totalPartsTableQuery;
        const partCountTableData = await this.queryRunner.runQuery.perform('BuildStatuses', andonProductionQuery);
        let goodParts = 0;
        let badParts = 0;
        const rows = partCountTableData.map((row)=>{
            goodParts += row.goodParts;
            badParts += row.totalParts - row.goodParts;
            return {
                station: row.station?.text.toUpperCase(),
                goodParts: row.goodParts,
                badParts: row.totalParts - row.goodParts
            };
        });
        return {
            rows,
            goodParts,
            badParts,
            exportData: ()=>rows.map(({ partCounts, ...row })=>row)
        };
    }));
    partCountData = trackedTask(this, this.getData, ()=>[
            this.params,
            this.timerToken,
            this.args.refresher
        ]);
    formatPartsColumnData(cell) {
        const { goodParts, badParts } = cell.getData();
        return `<div>
      <span class="part-count-good">${goodParts} </span>
      <span class="part-count-bad">${badParts}</span>
    </div>`;
    }
    static{
        template_6fe4a593d63a4b498cc3c52cebb7bf7f(`
    {{#if this.partCountData.value.rows.length}}
      <div
        class="
          station-count-{{this.partCountData.value.rows.length}}
          component-bi-andon-production-station
          bi-tabulator
          d-flex
          flex-column
          h-100
        "
        ...attributes
      >
        <TabulatorTable
          class="production-tabulator"
          @tableConfiguration={{this.tableConfiguration}}
          @data={{this.partCountData.value.rows}}
          @exportDataFunction={{this.partCountData.value.exportData}}
          @exportFilename={{t 'bi.chartLabel.partCounts'}}
          {{didInsert this.startAutoRefresh.perform}}
        />
        <div class="d-flex justify-content-center p-2">
          <BiTotalCyclesLabel
            @total={{this.partCountData.value.goodParts}}
            @rejectCount={{this.partCountData.value.badParts}}
          />
        </div>
      </div>
    {{else if this.partCountData.isRunning}}
      <Spinner @belowSidePanel={{true}} />
    {{else}}
      <BiNoDataPlaceholder />
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
